import { Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyledEngineProvider } from "@mui/material";
import { Amplify } from "aws-amplify";
import { I18nextProvider } from "react-i18next";
import { HistoryRouter as Router } from "redux-first-history/rr6";

import { MzSnackbarProvider } from "@markaaz/ui/MzToast";
import theme from "@markaaz/ui/theme";
import store, { history } from "./store";
import HomePage from "./pages/home/HomePage";
import OverviewPage from "pages/OverviewPage";
import PortfolioPage from "pages/PortfolioPage";
import OnboardingPage from "pages/OnboardingPage";
import MonitoringPage from "pages/MonitoringPage";
import AdministrationPage from "./pages/SystemSettingsPage";
import amplifyConfig from "./api/amplify";
import AuthGuard from "pages/home/AuthGuard";
import i18n from "./i18n";
import "@progress/kendo-theme-default/dist/all.css";
import "./App.css";
import AuthPage from "pages/auth/AuthPage";
import LoginPage from "pages/auth/LoginPage";
import RegisterPage from "pages/auth/RegisterPage";
import ForgotPasswordPage from "pages/auth/ForgotPasswordPage";
import ApprovalPage from "pages/ApprovalPage";
import AuthLayoutPage from "pages/auth/AuthLayoutPage";
import VerifyInvitePage from "pages/auth/VerifyInvitePage";
import UserManagementPage from "pages/UserManagementPage";
import BusinessLookupPage from "pages/BusinessLookupPage";
import TraceSearchPage from "pages/TraceSearchPage";
import ApiUsageReportsPage from "pages/ApiUsageReportsPage";
import MonitoringReportsPage from "pages/MonitoringReportsPage";
import MyAccountPage from "pages/MyAccountPage";
import FileValidationPage from "pages/FileValidationPage";
import BatchConfigurationPage from "pages/BatchConfigurationPage";
import BatchSummaryPage from "pages/BatchSummaryPage";
import MobileBlockerScreen from "components/common/MobileBlocker/MobileBlockerScreen";
import useMobileBrowser from "utils/useMobileBrowser";

Amplify.configure(amplifyConfig);

function App() {
  const isMobile = useMobileBrowser();

  if (isMobile && window.location.pathname !== "/mobileCheck") {
    window.location = `${window.location.origin}/mobileCheck`;
  }

  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <Router history={history}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MzSnackbarProvider />
                <Routes>
                  <Route
                    path="/"
                    element={
                      <AuthGuard>
                        <HomePage />
                      </AuthGuard>
                    }
                  >
                    <Route index path="/overview/:tab" element={<OverviewPage />} />
                    <Route path="/verification-api-setup/:tab" element={<OnboardingPage />} />
                    <Route path="/monitoring-file-upload" element={<PortfolioPage />} />
                    <Route path="/monitoring-setting/:tab" element={<MonitoringPage />} />
                    <Route path="/api-usage-reports" element={<ApiUsageReportsPage />} />
                    <Route path="/monitoring-reports" element={<MonitoringReportsPage />} />
                    <Route path="system-level-settings/:tab" element={<AdministrationPage />} />
                    <Route path="/user-management" element={<UserManagementPage />} />
                    <Route path="/business-lookup" element={<BusinessLookupPage />} />
                    <Route path="/my-account" element={<MyAccountPage />} />
                    <Route path="/batch/file-validation" element={<FileValidationPage />} />
                    <Route path="/batch/summary" element={<BatchSummaryPage />} />
                    <Route path="/batch-setup/configuration" element={<BatchConfigurationPage />} />
                    <Route path="/trace" element={<TraceSearchPage />} />
                  </Route>
                  <Route element={<AuthPage />}>
                    <Route path="/sign-in" element={<LoginPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/sign-up/invite/:requestId" element={<VerifyInvitePage />} />
                  </Route>
                  <Route element={<AuthLayoutPage />}>
                    <Route path="/sign-up" element={<RegisterPage />} />
                  </Route>
                  <Route path="/:url/:requestId/:status" element={<ApprovalPage />} />
                  <Route path="/mobileCheck" element={<MobileBlockerScreen />} />
                </Routes>
              </LocalizationProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </Router>
      </I18nextProvider>
    </Provider>
  );
}

export default App;
